import { Grid, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import UiCard from "components/base/UiCard";
import UiSideModal from "components/base/UiSideModal";
import { CustomGetProposalQueryData } from "hooks/proposals/useCustomGetProposal";
import useModalState from "hooks/useModalState";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import DraftDiscountDrawer from "./DraftDiscountDrawer";
import DraftSalesTax from "./DraftSalesTax";
import LabeledValue from "./LabeledValue";
import MarginsDrawer from "./MarginsDrawer";
import { draftColors } from "./draftDesignTokens";

type FinancialsCardProps = {
  proposal: NonNullable<CustomGetProposalQueryData>;
};

export default function FinancialsCard({ proposal }: FinancialsCardProps) {
  const taxDrawer = useModalState();
  const discountDrawer = useModalState();

  const directCosts = formatCentsToUSD(proposal.costDirect ?? 0);
  const overheadCosts = formatCentsToUSD(proposal.costOverhead ?? 0);
  const indirectCosts = formatCentsToUSD(proposal.costIndirect ?? 0);
  const costTotal = formatCentsToUSD(proposal.costTotal ?? 0);

  const profitPercentage = proposal.profitPercentage ?? 0;
  const discountCents = +(proposal.discount ?? 0);
  const discount = formatCentsToUSD(discountCents);
  const profit = formatCentsToUSD(+(proposal.profit ?? 0) - discountCents);
  const subtotal = formatCentsToUSD(+(proposal.overallTotal ?? 0));

  const taxAmount = +(proposal.costTaxes ?? 0);
  const overallTotalAfterTaxes =
    proposal.overallTotalAfterTaxes ?? +(proposal.overallTotal ?? 0) - discountCents;
  const total = formatCentsToUSD(overallTotalAfterTaxes);

  return (
    <>
      <UiCard variant="info">
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap="8px">
          <Grid container spacing="12px" sx={{ flex: 1, minWidth: 0 }}>
            <Grid item xs={12} sm={6} className="tour-totals-costs">
              <MDBox display="flex" flexDirection="column" gap="6px">
                <LabeledValue label="Direct Costs" value={directCosts} boldValue />
                <LabeledValue label="Overhead Costs" value={overheadCosts} />
                <LabeledValue label="Indirect Costs" value={indirectCosts} />
                <LabeledValue label="Total Cost" value={costTotal} boldValue />
              </MDBox>
            </Grid>
            <Grid item xs={12} sm={6} className="tour-totals-profit">
              <MDBox display="flex" flexDirection="column" gap="6px">
                <LabeledValue label={`Profit (${profitPercentage}%)`} value={profit} boldValue />
                <LabeledValue label="Subtotal" value={subtotal} boldValue />
                <LabeledValue
                  label={
                    <MDBox
                      component="span"
                      display="inline-flex"
                      alignItems="center"
                      gap="4px"
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={discountDrawer.onOpen}
                    >
                      Discount
                      <Icon
                        sx={{
                          fontSize: "12px !important",
                          color: draftColors.textSecondary,
                        }}
                      >
                        edit
                      </Icon>
                    </MDBox>
                  }
                  value={discount}
                />
                <LabeledValue
                  label={
                    <MDBox
                      component="span"
                      display="inline-flex"
                      alignItems="center"
                      gap="4px"
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={taxDrawer.onOpen}
                    >
                      Tax
                      <Icon
                        sx={{
                          fontSize: "12px !important",
                          color: draftColors.textSecondary,
                        }}
                      >
                        edit
                      </Icon>
                    </MDBox>
                  }
                  value={formatCentsToUSD(taxAmount)}
                />
                <LabeledValue label="Total" value={total} boldValue />
              </MDBox>
            </Grid>
          </Grid>
          <MarginsDrawer proposal={proposal} />
        </MDBox>
      </UiCard>

      <UiSideModal
        open={taxDrawer.open}
        onClose={taxDrawer.onClose}
        title="Sales Tax"
        width="420px"
        closeButtonClassName="tour-tax-drawer-close"
      >
        <DraftSalesTax onClose={taxDrawer.onClose} />
      </UiSideModal>

      <DraftDiscountDrawer
        open={discountDrawer.open}
        onClose={discountDrawer.onClose}
        proposal={proposal}
        closeButtonClassName="tour-discount-drawer-close"
      />
    </>
  );
}
